import { Injectable } from '@angular/core';

import { Observable, from, of, switchMap } from 'rxjs';

import { OneIdHybridGuest } from '../interfaces/one-id-hybrid-guest';
import { OneIdGuest } from '../interfaces/one-id-guest';
import { WindowRef } from '../../window-ref/window-ref.service';
import { CookieService } from 'ngx-cookie-service';
import { OneIdOptions } from '../interfaces/one-id-options';

export type OneIDEvents
    = 'age-gated' | 'close' | 'create' | 'error' | 'init' | 'login' | 'logout' | 'low-trust'
    | 'opt-in' | 'primary-account' | 'reauth' | 'refresh' | 'update';

@Injectable({
    providedIn: 'root',
})
export class OneIdService {
    private _guest: OneIdGuest;
    private _hybridGuest: OneIdHybridGuest;
    isOnIdInit: boolean;

    constructor(
        private windowRef: WindowRef,
        private cookieService: CookieService
    ) {}

    /**
     * Set guest from PEP object
     * @returns Promise<void>
     */
    public setGuestFromPepObject(): Promise<void> {
        return this.windowRef?.PEP.PROFILE_AUTH.getGuest(true).then((guest: OneIdGuest) => {
            this._guest = guest;
        });
    }

    /**
     * Set guest from hybrid environment
     * @params guest hybrid object
     */
    public setHybridGuest(hybridGuest: OneIdHybridGuest) {
        this._hybridGuest = hybridGuest;
    }

    /**
     * Get user loggedIn status
     * @returns Observable<any>
     */
    public getLoggedInStatus(): Observable<any> {
        return from(this.windowRef?.PEP.PROFILE_AUTH.getLoggedInStatus());
    }

    /**
     * Get guest object info
     * @returns Promise<object> If logged-in, returns Promise with guest object.
     * If not-logged in, return rejected promise.
     */
    public getGuest(fullPayload?: boolean): Promise<any> {
        return this.windowRef?.PEP.PROFILE_AUTH.getLoggedInStatus().then((status) =>
            status ? this.windowRef?.PEP.PROFILE_AUTH.getGuest(fullPayload) : Promise.reject('Guest is not logged In'),
        );
    }

    /**
     * Get guest object info as observable
     * @returns Observable<object> returns observable with guest object.
     */
    public getGuestObservable(fullPayload?: boolean): Observable<object> {
        return from(this.getGuest(fullPayload));
    }

    /**
     * Get user swid
     * @returns Observable<string> with a guest's swid
     */
    public getSwid(): Observable<string> {
        return from(this.getGuest().then((guest) => guest.token.swid));
    }

    /**
     * Get user token
     * @returns Observable<string> with a guest's token
     */
    public getAccessToken(): Observable<string> {
        return from(this.getGuest().then((guest) => guest.token.access_token));
    }

    /**
     * Get guest trust state
     * @returns Observable<boolean>
     */
    public getTrustStateStatus(): Observable<any> {
        return from(this.windowRef?.PEP.PROFILE_AUTH.getLoggedInStatus()).pipe(
            switchMap((status: boolean) => {
                if (status) {
                    return from(this.windowRef?.PEP.PROFILE_AUTH.getTrustStateStatus());
                } else {
                    return of(status);
                }
            })
        );
    }

    /**
     * Logout user
     * @returns Observable<any>
     */
    public logout(): Observable<any> {
        return from(this.windowRef?.PEP.PROFILE_AUTH.logout());
    }

    /**
     * Launches the OneID UI for the standard login (username/password) workflow.
     */
    public launchIdentityFlow(email?: string, options?: OneIdOptions): Promise<void> {
        if (email && !options) { 
            return this.windowRef?.PEP.PROFILE_AUTH.launchIdentityFlow(email);
        }

        if (email && options) { 
            return this.windowRef?.PEP.PROFILE_AUTH.launchIdentityFlow(email, options);
        }
                        
        return this.windowRef?.PEP.PROFILE_AUTH.launchIdentityFlow();
    }

    /**
     * Re Launch the OneID UI for the standard login (username/password) workflow.
     */
    public launchReauth(): Promise<void> {
        return this.windowRef?.PEP.PROFILE_AUTH.launchReauth();
    }

    /**
     * Return if oneId is initializated
     */
    public isOneIdInitializated() {
        return this.windowRef?.PEP.PROFILE_AUTH.isInitialized;
    }

     /**
     * Attach an event listener to an event emitted by the OneID client instance.
     * @param {string} [event] Event name.
     * @param {function} [listener] Event listener.
     */
     public on(event: OneIDEvents, listener: Function): void {
        this.windowRef?.PEP.PROFILE_AUTH.on(event, listener);
    }

    /**
     * Detach an event listener from an event.
     * @param {string} [event] Event name.
     * @param {function} [listener] Event listener. If no listener is provided, all listeners will be purged from the provided event.
     */
    public off(event: OneIDEvents, listener?: Function): void {
        this.windowRef?.PEP.PROFILE_AUTH.off(event, listener);
    }

    /**
     * Get guest info from PEP object
     * @returns guest object
     */
    get guest() {
        return this._guest;
    }

    /**
     * Get guest info hybrid environment
     * @returns guest hybrid object
     */
    get hybridGuest() {
        return this._hybridGuest;
    }

    /**
     * Get swid from guest or hybrid guest
     * Needs guest or hybrid guest object to be defined
     * @returns string
     */
    get swid() {
        if (this.windowRef.nativeWindow['disney'].device?.embedded) {
            return this.hybridGuest?.swid ?? this.cookieService.get('SWID');
        }

        return this.guest?.token?.swid;
    }

    /**
     * Get token from guest or hybrid guest
     * Needs guest or hybrid guest object to be defined
     * @returns string
     */
    get token() {
        return this.windowRef.nativeWindow['disney'].device?.embedded ? this.hybridGuest?.token : this.guest?.token?.access_token;
    }

    /**
     * Get token from guest or hybrid guest
     * Needs guest or hybrid guest object to be defined
     * @returns string
     */
    get oId() {
        return this.windowRef?.PEP.PROFILE_AUTH.did;
    }
}
